<template>
   <b-card class="study-setup-card">
      <div class="study-setup-card-header">
         <div
            class="card-name-wrapper"
            :class="{'card-name-wrapper--finished': hasCheckmark || isCardLocked}"
         >
            <p class="card-name" :class="{'card-name--finished': hasCheckmark || isCardLocked}">
               {{ cardName.toUpperCase() }}
            </p>
         </div>
         <b-button
            v-if="isEditVisible && !isCardLocked"
            :disabled="isDisabled || (disableEditWhenCompleted && completed)"
            size="small"
            variant="primary"
            class="action-button light-button edit-button"
            @click="onEditClick"
            >Edit</b-button
         >
      </div>
      <p class="card-title">
         {{ cardTitle }}
         <b-icon
            v-if="hasCheckmark || isCardLocked"
            icon="check"
            class="checkmark-icon"
            arria-hidden="true"
         ></b-icon>
      </p>
      <p class="card-description">
         {{ cardDescription }}
      </p>
      <div class="study-setup-card-footer">
         <b-button
            v-if="!otherInfoSection && notStarted && !wasNotUsed"
            :disabled="isDisabled"
            size="sm"
            variant="success"
            class="action-button start-button"
            @click="onStartClick()"
            >Start</b-button
         >
         <div v-if="wasNotUsed" class="not-used-wrapper">
            <b-icon-info-circle variant="primary" class="info-icon">Not used</b-icon-info-circle>
            <p class="info-text">{{ notUsedText }}</p>
         </div>
         <div v-if="!notStarted && !wasNotUsed" class="progress-wrapper">
            <div class="card-progress">
               <p class="section-count">
                  {{
                     cardName === 'Payroll'
                        ? progress.periodsWithEmp + '/' + progress.periodsCnt
                        : progress.docsCnt
                  }}
               </p>
               <p class="section-description">
                  <span v-if="cardName === 'Payroll'"
                     >Periods with
                     {{ progress.periodsWithEmp === 1 ? 'Employee' : 'Employees' }}</span
                  >
                  <span v-else
                     >Supporting {{ progress.docsCnt === 1 ? 'Document' : 'Documents' }}</span
                  >
               </p>
            </div>
            <div v-if="!otherInfoSection" class="card-progress">
               <p class="section-count">{{ sectionCount }}</p>
               <p class="section-description">{{ progressDescription }}</p>
            </div>
         </div>
         <b-button
            v-if="otherInfoSection && !wasNotUsed && !isCardLocked"
            :disabled="isDisabled"
            size="sm"
            variant="primary"
            class="action-button dark-button add-documents-button"
            @click="onStartClick()"
            >Add Documents</b-button
         >
      </div>
   </b-card>
</template>

<script>
import {mapGetters} from 'vuex';

export default {
   name: 'StudySetupCard',

   props: {
      cardName: String,
      cardTitle: String,
      cardDescription: String,
      progress: Object,
      progressDescription: String,
      otherInfoSection: Boolean,
      disableEditWhenCompleted: {
         type: Boolean,
         default: true,
      },
      completed: {
         type: Boolean,
         default: null,
      },
      notUsedText: {
         type: String,
         default: 'This section has not been used yet.',
      },
      wasUsed: {
         type: Boolean || null,
         default: null,
      },
      // Whether every supported period already has at least one payee for this section.
      // null means the caller has no such concept -- fall back to isCardCompleted instead.
      checkmarkOnData: {
         type: Boolean,
         default: null,
      },
   },

   computed: {
      ...mapGetters({
         activeStudy: 'companies/activeStudy',
         isActiveStudyInSetupStage: 'companies/isActiveStudyInSetupStage',
         uploadCategories: 'uploads/uploadCategories',
      }),

      isEditVisible() {
         const hasProgress = !!this.progress;
         const wasUsedSet = this.wasUsed !== null;

         if (!hasProgress && wasUsedSet && this.wasUsed) {
            return false;
         }

         return (hasProgress || wasUsedSet) && !this.otherInfoSection;
      },

      isCardCompleted() {
         if (this.completed !== null) {
            return this.completed;
         }
         return !!this.progress || this.wasNotUsed;
      },

      // The checkmark for employees/contractors/supplies/cloud reflects whether every
      // period has at least one payee, not whether the section was marked complete --
      // unlike the "finished" pill styling and the edit-lock, which stay tied to actual
      // completion. A section declared "not used" counts as satisfied either way.
      hasCheckmark() {
         if (this.checkmarkOnData !== null) {
            return this.checkmarkOnData || this.wasNotUsed;
         }
         return this.isCardCompleted;
      },

      isCardLocked() {
         const category = this.uploadCategories.find((c) => c.name === this.cardTitle);
         return category ? category.locked : false;
      },

      notStarted() {
         return !this.progress;
      },

      wasNotUsed() {
         if (this.wasUsed === null) {
            return false;
         }
         return !this.wasUsed;
      },

      sectionCount() {
         if (isNaN(this.progress.completed)) {
            return `${this.progress.cnt}`;
         }

         return `${this.progress.completed}/${this.progress.cnt}`;
      },

      isDisabled() {
         return this.activeStudy.studySetupCompleted && this.isActiveStudyInSetupStage;
      },
   },

   methods: {
      onStartClick() {
         this.$emit('onStart');
      },
      onEditClick() {
         this.$emit('onEdit');
      },
   },
};
</script>

<style scoped lang="scss">
.study-setup-card {
   min-height: 19.5rem;

   > .card-body {
      display: flex;
      flex-direction: column;
   }
}

.study-setup-card-header {
   display: flex;
   align-items: center;
   justify-content: space-between;
}

.action-button {
   font-size: 0.75rem;
}

.edit-button {
   width: 3.75rem;
}

.card-name-wrapper {
   padding: 0.5rem;
   background: #008fff1a;
   color: #1660a9;
   border-radius: 100px;

   &--finished {
      background: #cccccc80;
   }
}

.checkmark-icon {
   color: $success;
   font-size: 1.6rem;
   vertical-align: middle !important;
}

.card-name {
   font-size: 0.6875rem;
   font-weight: 500;
   line-height: 1.125rem;
   margin: 0;

   &--finished {
      color: #888888;
   }
}

.card-title {
   padding: 0.25rem 0;
   font-size: 1rem;
   color: black;
   font-weight: 600;
}

.card-description {
   white-space: pre-line;
   font-size: 0.75rem;
   color: $black;
   font-weight: 400;
}

.study-setup-card-footer {
   display: flex;
   align-items: flex-end;
   flex: 1;

   .add-documents-button {
      margin-left: auto;
   }
}

.start-button {
   width: 6rem;
}

.add-documents-button {
   width: 8.5rem;
}

.progress-wrapper {
   display: flex;
   gap: 1rem;
   align-items: center;
}

.card-progress {
   display: flex;
   gap: 1rem;
   min-width: 0;

   &--hidden {
      visibility: hidden;
   }

   p {
      margin: 0 auto;
   }
}

.section-count {
   display: flex;
   align-items: center;
   font-size: 2.125rem;
   font-weight: 700;
   line-height: 1.375rem;
   color: black;
   margin: 0;
}

.section-description {
   font-size: 0.625rem;
   line-height: 0.8125rem;
   color: #888888;
   font-weight: 400;
   flex: 0 0 auto;
   display: inline-block;
   inline-size: min-content;
   white-space: normal;
   overflow-wrap: break-word;
}

.not-used-wrapper {
   display: flex;
   align-items: center;
   gap: 0.5rem;
}

.info-icon {
   font-size: 0.75rem;
}

.info-text {
   font-size: 0.75rem;
   font-weight: 600;
   color: $primary;
   margin: 0;
}
</style>
